import { useLogout } from '@/data/Content/Logout';
import { useNotifications } from '@/data/Content/Notifications';
import { useLocalization } from "@/data/Localization";
import { useUser } from '@/data/User';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';

interface UserInactivityAlertProps {
    timeOut: string;
}

const UserInactivityAlert: React.FC<UserInactivityAlertProps> = ({ timeOut }) => {

    const SessionNLS = useLocalization('SessionError');
    const { user } = useUser();
    const loggedIn = user?.isLoggedIn ?? false;
    const { handleLogout } = useLogout();
    const InactivityAlertNLS = useLocalization('UserInactivityAlert');
    const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
    const [openInactivityAlertDialog, setOpenInactivityAlertDialog] = useState(false);
    const router = useRouter();
    const [popupTimer, setPopupTimer] = useState<NodeJS.Timeout | null>(null);
    const handleCloseShareDialog = () => {
        setOpenInactivityAlertDialog(false);
    };
    const { showSuccessMessage } = useNotifications();
    const resetTimer = () => {
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        if (loggedIn) {
            inactivityTimer.current = setTimeout(() => {
                setOpenInactivityAlertDialog(true);
            }, parseInt(timeOut));
        }
    };

    useEffect(() => {
        if (!loggedIn) return;

        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
        events.forEach(event =>
            window.addEventListener(event, resetTimer)
        );

        resetTimer();

        return () => {
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
            events.forEach(event =>
                window.removeEventListener(event, resetTimer)
            );
        };
    }, [loggedIn]);



    useEffect(() => {
        if (openInactivityAlertDialog) {
            const timer = setTimeout(() => {
                setOpenInactivityAlertDialog(false);
                setTimeout(() => {
                    handleLogout(new Event('logout') as any);
                    router.push('/');
                    showSuccessMessage(SessionNLS.TimeoutMsg.t());
                });
            }, 120000); // show pop up for 2 minutes and then logout the user and redirect to home page with a message

            setPopupTimer(timer);

            return () => {
                if (timer) clearTimeout(timer);
            };
        } else if (popupTimer) {
            clearTimeout(popupTimer);
        }
    }, [openInactivityAlertDialog]);

    const handleContinue = () => {
        handleCloseShareDialog();
        resetTimer();
    };

    const logoutUser = () => {
        handleCloseShareDialog();
        handleLogout(new Event('logout') as any);
    };

    return (
        <>
            {openInactivityAlertDialog && (
                <Dialog open={openInactivityAlertDialog} onClose={handleCloseShareDialog}>

                    <DialogTitle sx={{ fontSize: '22px' }}>{InactivityAlertNLS.title.t()}</DialogTitle>
                    <Typography px={3} py={0.5}> {InactivityAlertNLS.primaryText.t()}</Typography>
                    <Typography px={3} py={0.5}> {InactivityAlertNLS.secondaryText.t()}</Typography>
                    <DialogContent>


                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleContinue}
                            variant="contained"
                            color="secondary"
                            role="button"
                        >
                            {InactivityAlertNLS.continueSession.t()}
                        </Button>
                        <Button
                            id="logout-user"
                            data-testid="logout-user"
                            role="button"
                            type="submit"
                            variant="contained"
                            color="secondary"
                            onClick={logoutUser}
                            sx={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '2px solid black',
                                borderRadius: '5px',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: 'black',
                                },
                            }}
                        >
                            {InactivityAlertNLS.logoutSession.t()}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default UserInactivityAlert;